import React, { useEffect } from 'react';
import { Checkbox, Chip, FormControl, Grid, Tooltip, Typography } from '@mui/material';
import styles from './styles';
import { isMarketBeachfront } from 'src/utils/functions';
import { COLOR_GREY_400, COLOR_PRIMARY_1 } from 'src/colors';
import { AmenitiesIcons } from 'src/icons';
import FilterActionButton from './FilterActionButton';

interface AmenitiesFilterProps {
  filterVal: any;
  setFilterVal: any;
  isMobileView: boolean;
  setTotalFilterApplied: React.Dispatch<React.SetStateAction<number>>;
  handleCloseMenu: () => void;
  resetFilter: (filterNamesToReset: string[]) => void;
  isUserAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
  UpgradeCTA: (closeMenu: () => void) => JSX.Element;
  FilterCoingSoonComponent: React.ReactNode | null;
}

const categoriesObject = {
  beachfront: 'Beachfront',
  amazing_views: 'Amazing Views',
};

const commonAmenitiesObject = {
  pool: 'Pool',
  hot_tub: 'Hot Tub',
  parking: 'Parking',
  pets: 'Pet Friendly',
  ac: 'AC',
};

const amenitiesObject = {
  heating: 'Heating',
  breakfast: 'Breakfast',
  dryer: 'Dryer',
  washer: 'Washer',
  gym: 'Gym',
  kitchen: 'Kitchen',
  tv: 'TV',
  wifi: 'WiFi',
  bbq_grill: 'BBQ Grill',
  fireplace: 'Fireplace',
};

const getAmenityState = (checkboxType: 'with' | 'without', isChecked: boolean) => {
  if (checkboxType === 'with') {
    return isChecked ? true : null;
  } else {
    return isChecked ? false : null;
  }
};

const isEstimatorAdvancedFilter = false;

const AmenitiesFilter: React.FC<AmenitiesFilterProps> = ({
  filterVal,
  setFilterVal,
  isMobileView,
  setTotalFilterApplied,
  handleCloseMenu,
  resetFilter,
  isUserAllowed,
  handleOnSampleClick,
  UpgradeCTA,
  FilterCoingSoonComponent
}) => {
  useEffect(() => {
    let appliedCount = 0;
    if (filterVal && filterVal.amenities) {
      Object.values(filterVal.amenities).forEach(value => {
        if (value === true || value === false) {
          appliedCount++;
        }
      });
    }
    setTotalFilterApplied(appliedCount);
  }, [filterVal, setTotalFilterApplied]);

  const handleAmenitiesChange = (event, amenity) => {
    const checkboxType = getAmenityState(
      event.target.value as 'with' | 'without',
      event.target.checked
    );

    setFilterVal(prevFilterVal => {
      const newAmenitiesObject = { ...prevFilterVal.amenities };
      newAmenitiesObject[amenity] = checkboxType;

      return {
        ...prevFilterVal,
        amenities: newAmenitiesObject,
      };
    });

  };

  return (
    <Grid
      container
      direction="column"
      sx={{
        width: 'clamp(280px, 100vw, 400px)',
        px: 2.5,
        py: 1,
        zIndex: 999999,
        position: 'relative',
      }}
    >
      {!isUserAllowed && UpgradeCTA(handleCloseMenu)}
      {isUserAllowed && FilterCoingSoonComponent && FilterCoingSoonComponent}
      <Grid item>
        <Typography style={styles.filterTitleStyle}>Amenities</Typography>
      </Grid>
      <Grid item>
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={3}
        >
          {/* categories */}
          {!isEstimatorAdvancedFilter && (
            <Grid item>
              <FormControl style={{ width: '100%' }}>
                <Typography
                  color="textSecondary"
                  style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    marginBottom: '10px',
                  }}
                >
                  Categories
                  <Chip
                    label={'New'}
                    variant={'outlined'}
                    sx={{
                      minWidth: 50,
                      marginLeft: '10px',
                      background: 'linear-gradient(273.52deg, #7158E2 29.31%, #FF2079 97.77%)',
                      color: 'white',
                      fontWeight: 500,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        width: 'calc(100% - 5px)',
                        height: 'calc(100% - 4px)',
                        borderRadius: '999px',
                        backgroundColor: 'white',
                        position: 'absolute',
                        zIndex: 0,
                        margin: '10px',
                      },
                      '& .MuiChip-label': {
                        zIndex: 2,
                        color: 'black',
                        background:
                          '-webkit-linear-gradient(273.52deg, #7158E2 29.31%, #FF2079 97.77%)',
                        '-webkit-background-clip': 'text',
                        '-webkit-text-fill-color': 'transparent',
                      },
                    }}
                  />
                </Typography>

                <Grid
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ width: '100px' }}
                >
                  <Typography style={{ fontWeight: 500 }}>With</Typography>
                  <Typography style={{ fontWeight: 500, marginLeft: 2 }}>Without</Typography>
                </Grid>

                {[...Object.entries(categoriesObject)].map(([key, value]) => (
                  <Tooltip
                    title={
                      ''
                    }
                  >
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-start"
                      item
                      xs={12}
                    >
                      <Grid
                        container
                        item
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ width: '100px' }}
                      >
                        <Checkbox
                          value="with"
                          checked={filterVal.amenities[key] == true}
                          onChange={e => {
                            handleAmenitiesChange(e, key);
                          }}
                        />
                        <Checkbox
                          value="without"
                          checked={filterVal.amenities[key] == false}
                          onChange={e => {
                            handleAmenitiesChange(e, key);
                          }}
                        />
                      </Grid>

                      <Typography
                        style={{
                          marginLeft: '10px',
                          color: COLOR_PRIMARY_1,
                          fontWeight: 500,
                          alignItems: 'center',
                          display: 'flex',
                          gap: '10px',
                        }}
                      >
                        {AmenitiesIcons[key]} {value}
                      </Typography>
                    </Grid>
                  </Tooltip>
                ))}
              </FormControl>
            </Grid>
          )}
          {/* most commmon amenities */}
          <Grid item>
            <FormControl style={{ width: '100%' }}>
              <Typography
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#000',
                  marginBottom: '10px',
                }}
              >
                Most Common
              </Typography>

              <Grid
                container
                item
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                style={{ width: '100px' }}
              >
                <Typography style={{ fontWeight: 500 }}>With</Typography>
                <Typography style={{ fontWeight: 500, marginLeft: 2 }}>Without</Typography>
              </Grid>

              {[...Object.entries(commonAmenitiesObject)].map(([key, value]) => (
                <Tooltip
                  title={
                    // key == 'beachfront' && allListings.data && !isMarketBeachfront(allListings)
                    //   ? 'Market is not Beachfront'
                    //   : ''
                    ''
                  }
                >
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    item
                    xs={12}
                  >
                    <Grid
                      container
                      item
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{ width: '100px' }}
                    >
                      <Checkbox
                        value="with"
                        checked={filterVal.amenities[key] == true}
                        onChange={e => {
                          handleAmenitiesChange(e, key);
                        }}
                        // disabled={
                        //   key == 'beachfront' &&
                        //   allListings.data &&
                        //   !isMarketBeachfront(allListings)
                        // }
                      />
                      <Checkbox
                        value="without"
                        checked={filterVal.amenities[key] == false}
                        onChange={e => {
                          handleAmenitiesChange(e, key);
                        }}
                        // disabled={
                        //   key == 'beachfront' &&
                        //   allListings.data &&
                        //   !isMarketBeachfront(allListings)
                        // }
                      />
                    </Grid>

                    <Typography
                      style={{
                        marginLeft: '10px',
                        color: COLOR_PRIMARY_1,
                        //   key == 'beachfront' &&
                        //   allListings.data &&
                        //   !isMarketBeachfront(allListings)
                        //     ? COLOR_GREY_400
                        //     : COLOR_PRIMARY_1,
                        fontWeight: 500,
                        alignItems: 'center',
                        display: 'flex',
                        gap: '10px',
                      }}
                    >
                      {AmenitiesIcons[key]} {value}
                    </Typography>
                  </Grid>
                </Tooltip>
              ))}
            </FormControl>
          </Grid>
          {/* additional amenities*/}
          <Grid item>
            <FormControl style={{ width: '100%' }}>
              <Typography
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#000',
                  marginBottom: '10px',
                }}
              >
                Additional
              </Typography>

              <Grid
                container
                item
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                style={{ width: '100px' }}
              >
                <Typography style={{ fontWeight: 500 }}>With</Typography>
                <Typography style={{ fontWeight: 500, marginLeft: 2 }}>Without</Typography>
              </Grid>

              {[...Object.entries(amenitiesObject)].map(([key, value]) => (
                <Tooltip
                  title={
                    // key == 'beachfront' && allListings.data && !isMarketBeachfront(allListings)
                    //   ? 'Market is not Beachfront'
                    //   : ''
                    ''
                  }
                >
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    item
                    xs={12}
                  >
                    <Grid
                      container
                      item
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{ width: '100px' }}
                    >
                      <Checkbox
                        value="with"
                        checked={filterVal.amenities[key] == true}
                        onChange={e => {
                          handleAmenitiesChange(e, key);
                        }}
                        // disabled={
                        //   key == 'beachfront' &&
                        //   allListings.data &&
                        //   !isMarketBeachfront(allListings)
                        // }
                      />
                      <Checkbox
                        value="without"
                        checked={filterVal.amenities[key] == false}
                        onChange={e => {
                          handleAmenitiesChange(e, key);
                        }}
                        // disabled={
                        //   key == 'beachfront' &&
                        //   allListings.data &&
                        //   !isMarketBeachfront(allListings)
                        // }
                      />
                    </Grid>

                    <Typography
                      style={{
                        marginLeft: '10px',
                        color: COLOR_PRIMARY_1,
                        // key == 'beachfront' &&
                        //    allListings.data &&
                        //   !isMarketBeachfront(allListings)
                        //     ? COLOR_GREY_400
                        //      : COLOR_PRIMARY_1,
                        fontWeight: 500,
                        alignItems: 'center',
                        display: 'flex',
                        gap: '10px',
                      }}
                    >
                      {AmenitiesIcons[key]} {value}
                    </Typography>
                  </Grid>
                </Tooltip>
              ))}
            </FormControl>
          </Grid>
        </Grid>
        <Grid item>
          <FilterActionButton
            handleCloseMenu={handleCloseMenu}
            resetFilter={() => resetFilter(['amenities'])}
            isUserAllowed={isUserAllowed}
            handleOnSampleClick={handleOnSampleClick}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AmenitiesFilter;
